import { useNavigate } from 'react-router-dom';
import PageNavbar from '../../../components/feature/PageNavbar';

export default function PabloPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: 'ri-hand-heart-line',
      title: 'Ergonomic Design',
      description: 'Comfortable handheld device that fits naturally in the patient\'s hand for extended therapy sessions'
    },
    {
      icon: 'ri-sensor-line',
      title: 'Precision Sensors',
      description: 'Advanced sensors capture detailed hand movement data including grip strength, range of motion, and coordination'
    },
    {
      icon: 'ri-gamepad-line',
      title: 'Interactive Exercises',
      description: 'Engaging digital games and activities that make hand therapy enjoyable and motivating'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Real-time Feedback',
      description: 'Instant visual and audio feedback helps patients understand and improve their performance'
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Adjustable Difficulty',
      description: 'Therapy programs can be customized to match patient abilities and progression levels'
    },
    {
      icon: 'ri-file-chart-line',
      title: 'Comprehensive Reports',
      description: 'Detailed analytics and progress tracking for therapists to monitor patient improvements'
    }
  ];

  const assessmentCapabilities = [
    {
      title: 'Grip Strength',
      description: 'Measures maximum grip force and sustained grip endurance',
      icon: 'ri-hand-coin-line'
    },
    {
      title: 'Range of Motion',
      description: 'Tracks wrist and finger movement angles and flexibility',
      icon: 'ri-compass-3-line'
    },
    {
      title: 'Coordination',
      description: 'Evaluates hand-eye coordination and movement precision',
      icon: 'ri-focus-3-line'
    },
    {
      title: 'Reaction Time',
      description: 'Assesses speed of response to visual and tactile stimuli',
      icon: 'ri-timer-flash-line'
    },
    {
      title: 'Fine Motor Control',
      description: 'Tests dexterity and ability to perform precise movements',
      icon: 'ri-hand-heart-line'
    },
    {
      title: 'Bilateral Function',
      description: 'Compares performance between left and right hands',
      icon: 'ri-contrast-2-line'
    }
  ];

  const trainingActivities = [
    'Grip strengthening exercises',
    'Finger isolation movements',
    'Wrist rotation and flexion tasks',
    'Pinch grip training',
    'Hand opening and closing drills',
    'Coordination and timing challenges',
    'Endurance building activities',
    'Functional task simulation'
  ];

  const suitableConditions = [
    {
      condition: 'Stroke Recovery',
      description: 'Helps restore hand function and grip strength after stroke',
      icon: 'ri-heart-pulse-line'
    },
    {
      condition: 'Traumatic Brain Injury',
      description: 'Supports hand motor recovery following brain trauma',
      icon: 'ri-mental-health-line'
    },
    {
      condition: 'Spinal Cord Injury',
      description: 'Assists in regaining hand and wrist function',
      icon: 'ri-shield-cross-line'
    },
    {
      condition: 'Arthritis',
      description: 'Maintains hand mobility and reduces stiffness',
      icon: 'ri-hand-heart-line'
    },
    {
      condition: 'Post-Surgical Rehabilitation',
      description: 'Facilitates recovery after hand or wrist surgery',
      icon: 'ri-surgical-mask-line'
    },
    {
      condition: 'Peripheral Nerve Injury',
      description: 'Promotes nerve regeneration and motor relearning',
      icon: 'ri-pulse-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <PageNavbar />

      {/* Hero Section */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Precision Hand & Wrist Rehabilitation</h2>
              <p className="text-gray-600 text-base leading-relaxed mb-6">
                Pablo is an innovative handheld rehabilitation device designed for comprehensive hand and wrist therapy. 
                With its ergonomic design and advanced sensor technology, Pablo provides precise assessment and targeted 
                training for patients recovering from hand injuries, neurological conditions, or surgical procedures.
              </p>
              <p className="text-gray-600 text-base leading-relaxed">
                The device combines objective measurement capabilities with engaging interactive exercises, making hand 
                therapy more effective and enjoyable. Pablo's real-time feedback system helps patients understand their 
                progress and motivates them to achieve their rehabilitation goals.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden bg-gray-50">
              <img
                src="https://public.readdy.ai/ai/img_res/8d5143404c9fee2ff64d443df7562fee.jpg"
                alt="Pablo Handheld Device"
                className="w-full h-96 object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Features - Paragraph Style */}
      <section className="py-12 px-6 border-t border-gray-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Key Features</h2>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 text-base leading-relaxed mb-6">
              <strong className="text-gray-900">Ergonomic Design:</strong> The comfortable handheld device fits naturally in the patient's hand, 
              allowing for extended therapy sessions without discomfort. Its intuitive form factor makes it accessible for patients 
              with varying levels of hand function.
            </p>

            <p className="text-gray-600 text-base leading-relaxed mb-6">
              <strong className="text-gray-900">Precision Sensors:</strong> Advanced sensor technology captures detailed hand movement data, 
              including grip strength, range of motion, and coordination patterns. This objective measurement capability eliminates 
              subjective assessment variability and provides quantifiable progress metrics.
            </p>

            <p className="text-gray-600 text-base leading-relaxed mb-6">
              <strong className="text-gray-900">Interactive Exercises:</strong> Engaging digital games and activities transform traditional 
              hand therapy into an enjoyable experience. The game-based approach increases patient motivation and therapy adherence, 
              leading to better outcomes.
            </p>

            <p className="text-gray-600 text-base leading-relaxed mb-6">
              <strong className="text-gray-900">Real-time Feedback:</strong> Instant visual and audio feedback helps patients understand 
              their performance during exercises. This immediate response system enables patients to adjust their technique and 
              maximize the effectiveness of each therapy session.
            </p>

            <p className="text-gray-600 text-base leading-relaxed mb-6">
              <strong className="text-gray-900">Adjustable Difficulty:</strong> Therapy programs can be customized to match individual 
              patient abilities and progression levels. As patients improve, the difficulty automatically adjusts to maintain 
              appropriate challenge levels.
            </p>

            <p className="text-gray-600 text-base leading-relaxed">
              <strong className="text-gray-900">Comprehensive Reports:</strong> Detailed analytics and progress tracking provide therapists 
              with valuable insights to monitor patient improvements and adjust treatment plans accordingly.
            </p>
          </div>
        </div>
      </section>

      {/* Assessment Capabilities - Grid Layout */}
      <section className="py-12 px-6 border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Assessment Capabilities</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Grip Strength</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Measures maximum grip force and sustained grip endurance</p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Range of Motion</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Tracks wrist and finger movement angles and flexibility</p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Coordination</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Evaluates hand-eye coordination and movement precision</p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Reaction Time</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Assesses speed of response to visual and tactile stimuli</p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Fine Motor Control</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Tests dexterity and ability to perform precise movements</p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Bilateral Function</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Compares performance between left and right hands</p>
            </div>
          </div>
        </div>
      </section>

      {/* Training Activities - Compact List */}
      <section className="py-12 px-6 border-t border-gray-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Training Activities</h2>
          
          <div className="columns-2 gap-8">
            {trainingActivities.map((activity, index) => (
              <p key={index} className="text-gray-600 text-base mb-3 break-inside-avoid">
                {activity}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Suitable Conditions - Definition List */}
      <section className="py-12 px-6 border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Suitable For</h2>
          
          <dl className="space-y-6">
            {suitableConditions.map((item, index) => (
              <div key={index} className="border-l-2 border-gray-300 pl-6">
                <dt className="text-lg font-bold text-gray-900 mb-2">{item.condition}</dt>
                <dd className="text-gray-600 text-base leading-relaxed">{item.description}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 px-6 border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Why Choose Pablo?</h2>
          
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-6">
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">Portable & Convenient</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Compact design allows therapy in clinic, home, or any setting
              </p>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">Objective Measurement</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Precise data collection eliminates subjective assessment variability
              </p>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">Patient Engagement</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Game-based exercises increase motivation and therapy adherence
              </p>
            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 mb-2">Evidence-Based</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Quantifiable progress data supports clinical decision-making
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-8 px-6 border-t border-gray-100 mt-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            © 2025 Accelerated Recovery Centre. All rights reserved.
          </p>
          <a 
            href="https://readdy.ai/?ref=logo" 
            target="_blank" 
            rel="nofollow noopener noreferrer"
            className="text-gray-400 text-xs hover:text-gray-600 transition-colors mt-2 inline-block cursor-pointer"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
